/**
 * EnchanterModal.ts - Dedicated Enchanter NPC Workshop Modal
 * 
 * 인챈터 NPC에게 장비를 건네면 장비의 퀄리티(60%~100%)와 레벨에 따라
 * 무작위로 +전문스킬 및 +스탯 옵션을 인챈트해주는 전용 공방 창.
 */

import { EntityState, InventoryItem, CraftedEquipment } from '../../core/Types';
import { EventBus } from '../../core/EventBus';
import { Currency } from '../../core/Currency';
import { CLASS_SKILL_DEFINITIONS } from '../combat/CombatTypes';
import { enchantEquipmentByNpc } from '../character/CharacterSystem';

export const ENCHANT_COST_COPPER = Currency.toCopper({ gold: 100 }); // 100 골드

export class EnchanterModal {
  private el: HTMLElement | null = null;
  private isOpen = false;
  private player: EntityState | null = null;
  private enchanter: EntityState | null = null;
  private selectedSlotIndex: number | null = null;

  constructor() {
    this.createDom();
  }

  private createDom(): void {
    if (document.getElementById('daoc-enchanter-modal')) return;

    const wrap = document.createElement('div');
    wrap.id = 'daoc-enchanter-modal';
    wrap.style.cssText = `
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 540px;
      max-width: 95vw;
      background: linear-gradient(180deg, #1e1b4b, #0f172a);
      border: 2px solid #a855f7;
      border-radius: 8px;
      box-shadow: 0 0 0 1px #000, 0 0 25px rgba(168, 85, 247, 0.45);
      color: #e2e8f0;
      font-family: 'Segoe UI', sans-serif;
      z-index: 10000;
      display: none;
      flex-direction: column;
      user-select: none;
    `;

    document.body.appendChild(wrap);
    this.el = wrap;
  }

  public open(player: EntityState, enchanter: EntityState): void {
    this.player = player;
    this.enchanter = enchanter;
    this.selectedSlotIndex = null;
    this.isOpen = true;
    this.render();
  }

  public close(): void {
    this.isOpen = false;
    if (this.el) {
      this.el.style.display = 'none';
      this.el.innerHTML = '';
    }
  }

  public render(): void {
    if (!this.el || !this.player || !this.isOpen) return;
    this.el.style.display = 'flex';

    const p = this.player;
    const inv = p.inventory || [];
    
    // Find all equipment in inventory that can be enchanted
    const equipItems: Array<{ item: InventoryItem; slotIdx: number }> = [];
    inv.forEach((it, idx) => {
      if (it && (it.category === 'weapon' || it.category === 'offhand' || it.category === 'armor' || it.craftedEquip)) {
        equipItems.push({ item: it, slotIdx: idx });
      }
    });

    const activeItemEntry = this.selectedSlotIndex !== null ? inv[this.selectedSlotIndex] : (equipItems[0]?.item || null);
    const activeSlotIdx = this.selectedSlotIndex !== null ? this.selectedSlotIndex : (equipItems[0]?.slotIdx ?? -1);

    const crafted: CraftedEquipment | undefined = activeItemEntry?.craftedEquip;
    const enchants = crafted?.enchants || [];

    const isAlreadyEnchanted = enchants.length > 0;

    const enchantsHtml = isAlreadyEnchanted
      ? enchants.map(e => `
          <div style="background:rgba(168,85,247,0.2); border:1px solid #c084fc; border-radius:4px; padding:4px 8px; font-weight:bold; font-size:11px; display:inline-flex; align-items:center; gap:4px; color:#f8fafc;">
            <span>✨</span>
            <span>${e.targetName} +${e.value}</span>
          </div>
        `).join('')
      : `<div style="font-size:11px; color:#94a3b8; font-style:italic;">부여된 마법부여(인챈트) 옵션이 없습니다. (인챈트 비용: 🪙100골드)</div>`;

    this.el.innerHTML = `
      <!-- Titlebar -->
      <div style="background:linear-gradient(90deg, #4c1d95, #6b21a8); padding:8px 14px; border-bottom:1px solid #a855f7; display:flex; justify-content:space-between; align-items:center; font-size:13px; font-weight:bold; color:#fde047;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span>🔮</span>
          <span>왕실 대인챈터 엘라라의 마법부여 공방 (인챈트 비용: 🪙100G)</span>
        </div>
        <button id="btn-enchant-close" style="background:none; border:none; color:#f3d98b; font-size:16px; cursor:pointer; font-weight:bold;">✕</button>
      </div>

      <div style="padding:14px; display:flex; flex-direction:column; gap:12px;">
        <!-- Guide Banner -->
        <div style="background:rgba(30,27,75,0.7); border:1px solid #7c3aed; border-radius:6px; padding:8px 12px; font-size:11px; color:#cbd5e1; line-height:1.45;">
          • 인챈트 비용: <b>🪙100골드</b> (장비 퀄리티에 따라 1~4개 랜덤 전문스킬/스탯 부여)<br/>
          • <b style="color:#f87171;">주의: 이미 인챈트된 장비는 마력이 고착되어 재인챈트가 불가능합니다.</b><br/>
          • 장비당 동일 스킬 옵션 합산은 <b>최대 +10 상한선</b>까지 적용됩니다.
        </div>

        <div style="display:grid; grid-template-columns:180px 1fr; gap:12px;">
          <!-- Left: Equipment Selection List from Inventory -->
          <div style="background:#0f172a; border:1px solid #334155; border-radius:6px; padding:8px; display:flex; flex-direction:column; gap:6px; max-height:220px; overflow-y:auto;">
            <div style="font-size:10.5px; font-weight:bold; color:#94a3b8; margin-bottom:2px;">인벤토리 장비 선택</div>
            ${equipItems.length === 0 ? `<div style="font-size:10.5px; color:#64748b; padding:10px 0; text-align:center;">인벤토리에 마법부여 가능한 장비가 없습니다.</div>` : ''}
            ${equipItems.map(entry => {
              const isSelected = entry.slotIdx === activeSlotIdx;
              const hasEnchant = (entry.item.craftedEquip?.enchants?.length ?? 0) > 0;
              return `
                <button data-select-enchant-slot="${entry.slotIdx}" style="
                  background:${isSelected ? '#6b21a8' : '#1e293b'};
                  border:1px solid ${isSelected ? '#d8b4fe' : '#475569'};
                  color:#f8fafc; padding:6px; border-radius:4px; cursor:pointer; text-align:left;
                  display:flex; align-items:center; gap:6px; font-size:11px;
                ">
                  <span style="font-size:14px;">${entry.item.icon}</span>
                  <span style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1;">${entry.item.name}</span>
                  ${hasEnchant ? `<span style="font-size:9px; color:#c084fc; background:rgba(168,85,247,0.25); padding:1px 4px; border-radius:3px;">완료</span>` : ''}
                </button>
              `;
            }).join('')}
          </div>

          <!-- Right: Enchant Workbench Slot -->
          <div style="background:#0f172a; border:1.5px solid #a855f7; border-radius:6px; padding:10px; display:flex; flex-direction:column; justify-content:space-between;">
            ${activeItemEntry ? `
              <div>
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
                  <span style="font-size:13.5px; font-weight:bold; color:#fde047;">${activeItemEntry.icon} ${activeItemEntry.name}</span>
                  <span style="font-size:10.5px; color:#38bdf8;">${crafted ? `퀄리티 ${crafted.quality}%` : '일반 장비'}</span>
                </div>
                
                <div style="font-size:11px; color:#cbd5e1; margin-bottom:8px; line-height:1.4;">
                  ${crafted 
                    ? `• 주부품: ${crafted.components.primary.name} (${crafted.components.primary.quality}%)<br/>• 보조1: ${crafted.components.secondary1.name} | 보조2: ${crafted.components.secondary2.name}`
                    : activeItemEntry.description
                  }
                </div>

                <div style="background:rgba(15,23,42,0.9); border:1px dashed ${isAlreadyEnchanted ? '#a855f7' : '#64748b'}; border-radius:5px; padding:8px; display:flex; flex-wrap:wrap; gap:6px; min-height:42px; align-items:center;">
                  ${enchantsHtml}
                </div>
              </div>

              <div style="margin-top:12px; display:flex; justify-content:space-between; align-items:center;">
                <span style="font-size:11px; color:#fde047; font-weight:bold;">
                  소지금: 🪙${Currency.format(p.gold ?? 0)}
                </span>
                ${isAlreadyEnchanted ? `
                  <button disabled style="
                    background:#334155; border:1px solid #475569; color:#94a3b8; padding:7px 16px; border-radius:5px;
                    cursor:not-allowed; font-weight:bold; font-size:11px;
                  ">
                    🔒 이미 인챈트됨 (재인챈트 불가)
                  </button>
                ` : `
                  <button id="btn-execute-enchant" style="
                    background:linear-gradient(135deg, #7e22ce, #a855f7);
                    border:1px solid #d8b4fe; color:#ffffff; padding:7px 16px; border-radius:5px;
                    cursor:pointer; font-weight:bold; font-size:11.5px; box-shadow:0 0 10px rgba(168,85,247,0.5);
                  ">
                    🔮 100골드 지불하고 인챈트 실행
                  </button>
                `}
              </div>
            ` : `
              <div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; color:#64748b; font-size:12px; gap:8px;">
                <span style="font-size:28px;">🪄</span>
                <span>왼쪽 목록에서 마법을 부여할 장비를 선택하세요.</span>
              </div>
            `}
          </div>
        </div>
      </div>
    `;

    this.bindEvents(activeSlotIdx);
  }

  private bindEvents(activeSlotIdx: number): void {
    if (!this.el || !this.player) return;

    this.el.querySelector('#btn-enchant-close')?.addEventListener('click', () => this.close());

    this.el.querySelectorAll('[data-select-enchant-slot]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const slot = parseInt(btn.getAttribute('data-select-enchant-slot') || '-1', 10);
        if (slot >= 0) {
          this.selectedSlotIndex = slot;
          this.render();
        }
      });
    });

    this.el.querySelector('#btn-execute-enchant')?.addEventListener('click', () => {
      if (!this.player) return;
      const inv = this.player.inventory || [];
      const item = inv[activeSlotIdx];
      if (!item) return;

      // 1. 이미 인챈트된 장비인지 재검증 (재인챈트 불가)
      if (item.craftedEquip && item.craftedEquip.enchants && item.craftedEquip.enchants.length > 0) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [인챈터 엘라라] 이미 마법이 부여된 장비 [${item.name}] 은(는) 마력이 고착되어 재인챈트할 수 없습니다!`,
          category: 'system',
        });
        return;
      }

      // 2. 인챈트 비용 확인 및 차감 (1회당 100 골드 = 100,000,000 코퍼)
      const currentGold = this.player.gold ?? 0;
      if (currentGold < ENCHANT_COST_COPPER) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [인챈트 비용 부족] 마법부여 비용 100골드(${Currency.format(ENCHANT_COST_COPPER)})가 부족합니다! (현재 소지금: ${Currency.format(currentGold)})`,
          category: 'system',
        });
        return;
      }

      // 골드 차감
      this.player.gold = currentGold - ENCHANT_COST_COPPER;

      // If item doesn't have craftedEquip wrapper yet, build one
      if (!item.craftedEquip) {
        item.craftedEquip = {
          id: item.id,
          name: item.name,
          category: item.category === 'offhand' ? 'plate_shield' : 'sword_axe',
          slot: item.category === 'offhand' ? 'offhand' : item.category === 'armor' ? 'armor' : 'weapon',
          itemLevel: this.player.level || 25,
          quality: 92, // Default 92% quality for standard equipment
          components: {
            primary: { id: 'p1', name: '표준 단조 부품', partType: 'primary', skillUsed: 'metalworking', quality: 92 },
            secondary1: { id: 'p2', name: '보강 지지 부품', partType: 'secondary1', skillUsed: 'woodworking', quality: 92 },
            secondary2: { id: 'p3', name: '가죽 체결 부품', partType: 'secondary2', skillUsed: 'leatherworking', quality: 92 },
          },
          attackPowerMod: 1.0,
          speedSpMod: 0,
          durabilityMod: 1.0,
          weightKg: item.weightKg,
          enchants: [],
        };
      }

      // Generate random enchants based on character class
      const skillSet = CLASS_SKILL_DEFINITIONS[this.player.role] || CLASS_SKILL_DEFINITIONS.tank;
      const nameMap: Record<number, string> = {};
      for (const k in skillSet) nameMap[Number(k)] = skillSet[Number(k)].nameKr;

      enchantEquipmentByNpc(item.craftedEquip, this.player, nameMap);

      const summary = item.craftedEquip.enchants.map((e) => `${e.targetName} +${e.value}`).join(', ');
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🔮 [인챈터 엘라라] 100골드를 소모하여 [${item.name}] 에 마법부여 완료! ➔ [ ${summary} ] (재인챈트 불가)`,
        category: 'crafting',
      });

      this.render();
    });
  }
}

export const enchanterModalInstance = new EnchanterModal();
